import { GeneralsMealPreferencesAvoid } from "@/clients/models/GeneralsMealPreferencesAvoid";
import { QueryKeys } from "@/constants/query-keys";
import { useGetFoodAvoidOptions } from "@/hooks/requests/food-avoids/use-get-food-avoid-options";
import { useGetFoodAvoids } from "@/hooks/requests/food-avoids/use-get-food-avoids";
import { useUpdateFoodAvoids } from "@/hooks/requests/food-avoids/use-update-food-avoids";
import { useToast } from "@/hooks/ui/use-toast";
import { setProfileContext } from "@/utils/sentry/tools";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

export const useFoodAvoids = (onSuccess: () => void) => {
  const { data: foodAvoidOptions, isLoading: isOptionsLoading } =
    useGetFoodAvoidOptions();
  const { foodAvoids, isLoading: isFoodAvoidsLoading } = useGetFoodAvoids();
  const queryClient = useQueryClient();
  const { updateFoodAvoids, isUpdating } = useUpdateFoodAvoids();

  const [selectedFoods, setSelectedFoods] = useState<Set<string>>(new Set());
  const [customFoods, setCustomFoods] = useState<string[]>([]);
  const { showError } = useToast();
  const isLoading = isOptionsLoading || isFoodAvoidsLoading;
  const sortedFoodList = useMemo(() => {
    const defaultFoods =
      foodAvoidOptions?.avoidOptions
        .filter((food) => !customFoods?.includes(food))
        .sort() ?? [];
    return [...defaultFoods, ...customFoods];
  }, [foodAvoidOptions, foodAvoids, customFoods]);

  useEffect(() => {
    if (foodAvoidOptions && foodAvoids) {
      const customFoods = foodAvoids.filter(
        (food) => !foodAvoidOptions.avoidOptions.includes(food),
      );
      setCustomFoods(customFoods);
      setSelectedFoods(new Set(foodAvoids));
    }
  }, [foodAvoidOptions, foodAvoids]);

  const addCustomFood = (food: string) => {
    const trimmed = food.trim();
    if (!trimmed) return;
    setCustomFoods((prev) => {
      if (prev.includes(trimmed) || foodAvoids?.includes(trimmed)) return prev;
      return [...prev, trimmed];
    });
    setSelectedFoods((prev) => {
      const next = new Set(prev);
      next.add(trimmed);
      return next;
    });
  };

  const removeCustomFood = (food: string) => {
    setCustomFoods((prev) => prev.filter((item) => item !== food));
    setSelectedFoods((prev) => {
      const next = new Set(prev);
      next.delete(food);
      return next;
    });
  };

  const handleSubmit = () => {
    const dataBody: GeneralsMealPreferencesAvoid = {
      foodToSkip: Array.from(selectedFoods),
    };
    updateFoodAvoids(dataBody, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.FOOD_AVOIDS] });
        setProfileContext(QueryKeys.FOOD_AVOIDS, dataBody);
        onSuccess();
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };
  return {
    selectedFoods,
    setSelectedFoods,
    handleSubmit,
    isUpdating,
    isLoading,
    sortedFoodList,
    addCustomFood,
    removeCustomFood,
    customFoods,
  };
};
